import { useState, type FormEvent } from 'react';

import { Button, Card, Field, Input } from '@/design-system';
import { errorMessage, isApiError } from '@/shared/api';

import { accountCopy } from '../copy';
import { useChangePassword } from '../hooks';

/** Minimaal wat de API met `StrongPassword` eist; de API controleert de rest. */
const MIN_PASSWORD_LENGTH = 12;

interface Errors {
    current?: string;
    password?: string;
    confirmation?: string;
}

function validate(current: string, password: string, confirmation: string): Errors {
    return {
        ...(current === '' ? { current: accountCopy.currentRequired } : {}),
        ...(password === ''
            ? { password: accountCopy.newRequired }
            : password.length < MIN_PASSWORD_LENGTH
              ? { password: accountCopy.newTooShort }
              : password === current
                ? { password: accountCopy.sameAsCurrent }
                : {}),
        ...(confirmation === ''
            ? { confirmation: accountCopy.confirmRequired }
            : confirmation !== password
              ? { confirmation: accountCopy.mismatch }
              : {}),
    };
}

/**
 * Je eigen wachtwoord wijzigen (`PATCH /auth/password`).
 *
 * Deze sessie blijft, alle andere vervallen: wie denkt dat iemand meekijkt, zet
 * zo in één keer iedereen buiten. De bevestiging komt letterlijk van de API.
 */
export function PasswordCard() {
    const change = useChangePassword();
    const [current, setCurrent] = useState('');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [errors, setErrors] = useState<Errors>({});

    const apiField = (field: string) =>
        isApiError(change.error) ? change.error.firstValidationError(field) : undefined;
    const shown: Errors = {
        current: errors.current ?? apiField('current_password'),
        password: errors.password ?? apiField('password'),
        confirmation: errors.confirmation ?? apiField('password_confirmation'),
    };
    const formError =
        change.error && !shown.current && !shown.password && !shown.confirmation
            ? errorMessage(change.error)
            : null;

    function submit(event: FormEvent) {
        event.preventDefault();

        const next = validate(current, password, confirmation);

        setErrors(next);

        if (next.current || next.password || next.confirmation) {
            return;
        }

        change.mutate(
            { currentPassword: current, password, passwordConfirmation: confirmation },
            {
                onSuccess: () => {
                    setCurrent('');
                    setPassword('');
                    setConfirmation('');
                },
            },
        );
    }

    return (
        <Card padding="lg" as="section" aria-labelledby="password-title">
            <h2
                id="password-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {accountCopy.passwordTitle}
            </h2>
            <p className="m-0 mt-1 mb-3 text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                {accountCopy.passwordIntro}
            </p>

            <form onSubmit={submit} noValidate className="grid gap-[10px]">
                <Field label={accountCopy.currentPassword} error={shown.current}>
                    <Input
                        type="password"
                        autoComplete="current-password"
                        value={current}
                        onChange={(event) => setCurrent(event.target.value)}
                    />
                </Field>
                <Field
                    label={accountCopy.newPassword}
                    description={accountCopy.newPasswordHint}
                    error={shown.password}
                >
                    <Input
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                    />
                </Field>
                <Field label={accountCopy.confirmPassword} error={shown.confirmation}>
                    <Input
                        type="password"
                        autoComplete="new-password"
                        value={confirmation}
                        onChange={(event) => setConfirmation(event.target.value)}
                    />
                </Field>
                <div>
                    <Button type="submit" variant="primary" size="md" disabled={change.isPending}>
                        {change.isPending ? accountCopy.passwordBusy : accountCopy.passwordSubmit}
                    </Button>
                </div>
            </form>

            {change.isSuccess ? (
                <p
                    role="status"
                    className="m-0 mt-3 text-[13px] leading-[1.5] text-[var(--accent-dark)]"
                >
                    {change.data}
                </p>
            ) : null}

            {formError ? (
                <p role="alert" className="m-0 mt-3 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {formError}
                </p>
            ) : null}
        </Card>
    );
}
